import { seededId } from '../ids';
import type { Mutation } from '../mutations';
import { spendingMutation, type BuildCtx, type SpendResult } from './types';

export interface DefaultCategory {
  key: string;
  name: string;
  icon: string; // icon name for later; v0.1 shows a colored dot
}

export const DEFAULT_CATEGORIES = [
  { key: 'food', name: 'Food', icon: 'utensils' },
  { key: 'transport', name: 'Transport', icon: 'bus' },
  { key: 'bills_utilities', name: 'Bills & Utilities', icon: 'receipt' },
  { key: 'shopping', name: 'Shopping', icon: 'bag' },
  { key: 'health', name: 'Health', icon: 'heart' },
  { key: 'fun', name: 'Fun', icon: 'sparkles' },
  { key: 'other', name: 'Other', icon: 'dots' },
] as const satisfies readonly DefaultCategory[];

/**
 * Defaults that v0.1.0 seeded and v0.1.1 dropped (essentials only). Quit Smoking (v0.3) and
 * Payments (v0.5) create these when they're turned on, on the same seeded ids.
 */
export const RETIRED_DEFAULT_CATEGORIES = [
  { key: 'cigarettes', name: 'Cigarettes' },
  { key: 'loan_payment', name: 'Loan payment' },
] as const;

export type DefaultCategoryKey = (typeof DEFAULT_CATEGORIES)[number]['key'];

export interface CategoryLike {
  id: string;
  key: string | null;
  name: string;
  hidden: boolean;
  position: number;
  deletedAt: string | null;
}

export type CategoryNameError = 'name_required' | 'name_too_long' | 'name_taken';

export const MAX_CATEGORY_NAME = 40;

function cleanName(name: string): string {
  return name.trim().replace(/\s+/g, ' ');
}

export function validateCategoryName(
  name: string,
  existing: readonly CategoryLike[],
  exceptId?: string,
): CategoryNameError | null {
  const n = cleanName(name);
  if (n === '') return 'name_required';
  if (n.length > MAX_CATEGORY_NAME) return 'name_too_long';
  const lower = n.toLowerCase();
  const taken = existing.some(
    (c) => c.deletedAt === null && c.id !== exceptId && cleanName(c.name).toLowerCase() === lower,
  );
  return taken ? 'name_taken' : null;
}

/**
 * Default categories the user doesn't have yet. Ids are seededId(userId, 'category', key) and every
 * insert is ifAbsent, so a default that was renamed, hidden or deleted on another device is never reset.
 */
export function buildSeedCategories(
  userId: string,
  existingKeys: ReadonlySet<string>,
  ctx: BuildCtx,
): Mutation[] {
  return DEFAULT_CATEGORIES.flatMap((c, i) => {
    if (existingKeys.has(c.key)) return [];
    const id = seededId(userId, 'category', c.key);
    return [
      spendingMutation(
        ctx,
        'categories',
        'insert',
        id,
        { key: c.key, name: c.name, icon: c.icon, hidden: false, position: i + 1 },
        { ifAbsent: true },
      ),
    ];
  });
}

export function buildAddCategory(
  name: string,
  existing: readonly CategoryLike[],
  ctx: BuildCtx,
): SpendResult<{ categoryId: string; mutation: Mutation }, CategoryNameError> {
  const error = validateCategoryName(name, existing);
  if (error) return { ok: false, error };
  const live = existing.filter((c) => c.deletedAt === null);
  const position = live.reduce((max, c) => Math.max(max, c.position), 0) + 1;
  const categoryId = ctx.newId();
  const mutation = spendingMutation(ctx, 'categories', 'insert', categoryId, {
    key: null,
    name: cleanName(name),
    icon: null,
    hidden: false,
    position,
  });
  return { ok: true, categoryId, mutation };
}

export function buildRenameCategory(
  id: string,
  name: string,
  existing: readonly CategoryLike[],
  ctx: BuildCtx,
): SpendResult<{ mutation: Mutation }, CategoryNameError> {
  const error = validateCategoryName(name, existing, id);
  if (error) return { ok: false, error };
  return {
    ok: true,
    mutation: spendingMutation(ctx, 'categories', 'update', id, { name: cleanName(name) }),
  };
}

export function buildSetCategoryHidden(id: string, hidden: boolean, ctx: BuildCtx): Mutation {
  return spendingMutation(ctx, 'categories', 'update', id, { hidden });
}

/** Soft delete. Entries keep their categoryId and read as uncategorized until it's restored. */
export function buildDeleteCategory(id: string, ctx: BuildCtx): Mutation {
  return spendingMutation(ctx, 'categories', 'delete', id);
}

/** Undo of a delete: a plain insert on the soft-deleted id (conventions §14), never ifAbsent. */
export function buildRestoreCategory(id: string, ctx: BuildCtx): Mutation {
  return spendingMutation(ctx, 'categories', 'insert', id, {});
}

/**
 * One-time v0.1.1 cleanup: soft-deletes the retired defaults (Cigarettes, Loan payment) where the
 * user never used them. A category is kept when any entry (deleted ones too) points at it, or when
 * the user renamed it (they made it their own). Already deleted rows produce nothing.
 */
export function buildRetireUnusedDefaults(
  userId: string,
  existing: readonly CategoryLike[],
  usedCategoryIds: ReadonlySet<string>,
  ctx: BuildCtx,
  /** Keys a module that is on now owns (Quit Smoking keeps 'cigarettes'); never retired. */
  keepKeys: ReadonlySet<string> = new Set(),
): Mutation[] {
  return RETIRED_DEFAULT_CATEGORIES.flatMap((d) => {
    if (keepKeys.has(d.key)) return [];
    const id = seededId(userId, 'category', d.key);
    const row = existing.find((c) => c.id === id);
    if (!row || row.deletedAt !== null || row.name !== d.name || usedCategoryIds.has(id)) return [];
    return [buildDeleteCategory(id, ctx)];
  });
}

/** orderedIds is the full order the user chose; positions become 1..n. */
export function buildReorderCategories(
  orderedIds: readonly string[],
  existing: readonly CategoryLike[],
  ctx: BuildCtx,
): Mutation[] {
  const byId = new Map(existing.map((c) => [c.id, c]));
  return orderedIds.flatMap((id, i) => {
    const current = byId.get(id);
    if (!current || current.position === i + 1) return [];
    return [spendingMutation(ctx, 'categories', 'update', id, { position: i + 1 })];
  });
}

export function sortCategories<T extends CategoryLike>(list: readonly T[]): T[] {
  return [...list].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
}
